import { CCFPackagingGroup } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import { Button } from '@shared/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@shared/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import { cn } from '@shared/lib/utils';
import { useState } from 'react';

interface PackagingTypePickerProps {
  groups: CCFPackagingGroup[];
  selectedGroupName: string;
  onSelectGroup: (groupName: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

/**
 * Searchable packaging type dropdown — a combobox over the CCF packaging
 * groups. Popover is modal so it scrolls and overlays correctly inside
 * dialogs.
 */
export function PackagingTypePicker({
  groups,
  selectedGroupName,
  onSelectGroup,
  disabled = false,
  isLoading = false,
}: PackagingTypePickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild className="w-full" disabled={disabled}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'flex h-10 flex-row items-center justify-between truncate font-normal shadow-none hover:bg-background',
            !selectedGroupName && 'text-gray-400 hover:text-gray-400'
          )}
        >
          <span className="overflow-hidden text-ellipsis">
            {selectedGroupName ||
              (isLoading ? 'Loading types…' : 'Select type…')}
          </span>
          <Icons.chevronDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command>
          <CommandInput placeholder="Search packaging types..." />
          <CommandList className="max-h-56">
            <CommandEmpty>No packaging types found.</CommandEmpty>
            <CommandGroup>
              {groups.map((group) => (
                <CommandItem
                  key={group.name}
                  value={group.name}
                  onSelect={() => {
                    onSelectGroup(group.name);
                    setOpen(false);
                  }}
                >
                  <Icons.check
                    className={cn(
                      'mr-2 size-4',
                      selectedGroupName === group.name
                        ? 'opacity-100'
                        : 'opacity-0'
                    )}
                  />
                  {group.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
